import { CronExpressionParser } from "cron-parser";
import cronstrue from "cronstrue";
import { byCodeUnit } from "../sort";

/**
 * Cron parsing, description and next-occurrence maths (FR-50, FR-51).
 *
 * Ported from skypoint-studio's `server/utils/cron-utils.ts`, which is itself a thin wrapper
 * over cron-parser 5. The port keeps the donor's three entry points (`friendlyToCron`,
 * `calculateNextRunAt`, `validateCronExpressions`) so a scheduler document migrated out of
 * Cosmos produces the same expressions, and adds the three this application needs:
 * `validateCron`, `describeCron` and `nextFireAt`.
 *
 * Two behaviours of the underlying library are load-bearing here and are pinned by tests:
 *
 * - `next()` is *strictly* after the reference date. A tick that runs at exactly the firing
 *   second therefore advances to the following occurrence rather than returning the one it
 *   just fired, which is what stops the due scan from firing the same minute twice.
 * - **A timezone cron-parser cannot resolve is not rejected at parse time.** `parse()`
 *   accepts `Mars/Phobos` happily and only throws later, from `next()`, with the opaque
 *   message "CronDate: unhandled timestamp". So every validation path here computes an
 *   occurrence rather than merely parsing, and `isValidTimezone` checks the name against
 *   ICU first so the user sees a field error about the timezone instead of about the cron.
 *
 * DST is the library's job, not ours (it wraps Luxon). Verified against
 * `America/Los_Angeles`: a daily expression inside the skipped spring-forward window lands
 * on the first hour that exists, and a daily expression inside the repeated fall-back hour
 * fires once, on the first of the two. Both are covered in `__tests__/cronUtils.dst.test.ts`.
 */

/** A standard 5-field expression: minute hour day-of-month month day-of-week. */
export const CRON_FIELD_COUNT = 5;

/** Matches the `schedules.cron` column. */
export const MAX_CRON_LENGTH = 200;

/** Matches the `schedules.timezone` column. */
export const MAX_TIMEZONE_LENGTH = 64;

/** One day of the year (round 10, C1): month 1..12 and day 1..31. */
export interface YearDate {
  month: number;
  day: number;
}

/** The friendly builder's shape, ported from Studio's `FriendlySchedule` (FR-50). */
export interface FriendlyTimesSchedule {
  frequency: "Daily" | "Weekly" | "Monthly" | "Yearly";
  /** 12-hour time strings, e.g. `["01:00AM", "07:15PM"]`. */
  cronSchedules: string[];
  /** 0 = Sunday .. 6 = Saturday; used when `frequency` is `Weekly`. */
  weekDays: number[];
  /** 1..31; used when `frequency` is `Monthly`. */
  monthDays: number[];
  /** Used (and required) when `frequency` is `Yearly` (round 10, C1). */
  yearDate?: YearDate;
}

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/**
 * The days a Yearly date may name in `month` (1..12): February has 28, as February 29 occurs only
 * in leap years and a schedule that runs once every four years is not a yearly one.
 */
export function daysInMonthForYearly(month: number): number {
  if (month === 2) return 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

export const FEBRUARY_29_ISSUE = "February 29 runs only in leap years; choose another day.";

/**
 * Why a Yearly date cannot be a schedule, with the field it names (`month` or `day`), or null.
 * April 31 does not exist; February 29 exists one year in four (round 10, C1).
 */
export function yearDateIssue(value: YearDate): { field: "month" | "day"; issue: string } | null {
  if (!Number.isInteger(value.month) || value.month < 1 || value.month > 12) {
    return { field: "month", issue: "Choose a month." };
  }
  if (!Number.isInteger(value.day) || value.day < 1)
    return { field: "day", issue: "Choose a day." };
  if (value.month === 2 && value.day === 29) return { field: "day", issue: FEBRUARY_29_ISSUE };
  if (value.day > daysInMonthForYearly(value.month)) {
    const name = MONTH_NAMES[value.month - 1] as string;
    return { field: "day", issue: `${name} has no day ${value.day}; choose another day.` };
  }
  return null;
}

/** The steps the interval frequency offers, in minutes. */
export const INTERVAL_MINUTES = [15, 30, 60] as const;
export type IntervalMinutes = (typeof INTERVAL_MINUTES)[number];

/**
 * Every N minutes between two whole hours of the day (2026-09-28, owner: "every 30 minutes from
 * 10 AM to 6 PM"). Still ONE five-field expression: the minute field is the step (`*\/30`, or `0`
 * for every hour) and the hour field is the window (`10-17`).
 *
 * The window includes `windowStart` and ends BEFORE `windowEnd`: 10:00 AM until 6:00 PM every 30
 * minutes runs at 10:00 AM, 10:30 AM, ... 5:30 PM. One expression cannot fire at 6:00 PM without
 * also firing at 6:30 PM (the minute and hour fields multiply), so the end is exclusive and the
 * editor says so. `windowEnd` `12:00AM` is midnight at the END of the day.
 */
export interface FriendlyIntervalSchedule {
  frequency: "Interval";
  intervalMinutes: IntervalMinutes;
  /** A whole hour, `10:00AM`. */
  windowStart: string;
  /** A whole hour, `06:00PM`; `12:00AM` is the end of the day. */
  windowEnd: string;
  /** 0 = Sunday .. 6 = Saturday; empty is every day. */
  weekDays: number[];
}

export type FriendlySchedule = FriendlyTimesSchedule | FriendlyIntervalSchedule;

export type CronValidation =
  { valid: true; cron: string; description: string; next: Date } | { valid: false; issue: string };

/**
 * Whether ICU knows this timezone name.
 *
 * Deliberately permissive about *form*: `Intl` accepts the fixed abbreviations (`EST`) and
 * is case-insensitive about the region (`America/los_angeles`), and so is Postgres, so
 * rejecting those here would refuse names the database would have stored happily. What it
 * catches is the name that does not exist at all, which is the one cron-parser hides until
 * it throws from `next()`.
 */
export function isValidTimezone(timezone: string): boolean {
  if (!timezone || timezone.length > MAX_TIMEZONE_LENGTH) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/** True when the expression has exactly the five fields the `cron` column holds. */
export function hasFiveFields(cron: string): boolean {
  return cron.trim().split(/\s+/).length === CRON_FIELD_COUNT;
}

/**
 * The next occurrence strictly after `after`.
 *
 * Throws rather than returning null: every caller here has already validated the expression,
 * so a failure at this point is a bug or a corrupted row, and a null would be written to
 * `next_fire_at` and leave the schedule dormant while still reading as enabled — the exact
 * failure the donor's `validateCronExpressions` was added to prevent.
 */
export function nextFireAt(cron: string, timezone: string, after: Date = new Date()): Date {
  if (!isValidTimezone(timezone)) {
    throw new Error(`Unknown timezone ${JSON.stringify(timezone)}`);
  }
  return CronExpressionParser.parse(cron, { currentDate: after, tz: timezone }).next().toDate();
}

/**
 * Round 9 (B-28): the firing minutes (epoch minutes, ascending) of an expression over the next
 * `days` days, at most `cap` of them, from one parsed iterator. Skip dates and pauses are ignored
 * on purpose: the clash warning asks "would these two fire together", not "will they this week".
 * An expression that does not parse answers none.
 */
export function firingMinutes(
  cron: string,
  timezone: string,
  from: Date = new Date(),
  days = 7,
  cap = 800,
): number[] {
  if (!isValidTimezone(timezone)) return [];
  const end = from.getTime() + days * 24 * 60 * 60 * 1000;
  const minutes: number[] = [];
  try {
    const walk = CronExpressionParser.parse(cron, { currentDate: from, tz: timezone });
    while (minutes.length < cap && walk.hasNext()) {
      const at = walk.next().toDate().getTime();
      if (at > end) break;
      minutes.push(Math.floor(at / 60_000));
    }
  } catch {
    return minutes;
  }
  return minutes;
}

/**
 * How far `nextFireAtExcluding` walks past excluded days before it answers "no upcoming run": two
 * years and two days (round 10 wave 2 review). A Yearly schedule's next firing is at most 366
 * days away, so when that one date is a skip date the year after it, at most 732 days away, is
 * still found. The 400 days of round 9 lost it and left `next_fire_at` NULL, which nothing
 * recomputes until a save.
 */
export const NEXT_FIRE_WALK_DAYS = 732;

const DAY_MS = 24 * 60 * 60 * 1000;

const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const dateFormatters = new Map<string, Intl.DateTimeFormat>();

function dateFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = dateFormatters.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    dateFormatters.set(timezone, formatter);
  }
  return formatter;
}

/** The calendar date of `at` on a wall clock in `timezone`, as `YYYY-MM-DD`. */
export function localDateOf(at: Date, timezone: string): string {
  const parts = dateFormatter(timezone).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((entry) => entry.type === type)?.value ?? "";
  return `${part("year").padStart(4, "0")}-${part("month")}-${part("day")}`;
}

/** True for a `YYYY-MM-DD` that names a real calendar day (`2026-02-30` does not). */
export function isRealDate(value: string): boolean {
  const match = LOCAL_DATE.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const at = new Date(Date.UTC(year, month - 1, day));
  return at.getUTCFullYear() === year && at.getUTCMonth() === month - 1 && at.getUTCDate() === day;
}

/** `YYYY-MM-DD` plus `days` calendar days. */
export function addDaysToDate(value: string, days: number): string {
  const match = LOCAL_DATE.exec(value);
  if (!match) throw new Error(`Not a date: ${JSON.stringify(value)}`);
  const at = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days));
  return at.toISOString().slice(0, 10);
}

/**
 * The first instant of a local calendar day in `timezone`: 00:00 there, or the first valid local
 * time when daylight saving skips midnight (Havana, Santiago). A binary search on the one fact
 * that is monotonic, "the local date at t is on or after `value`", so no offset arithmetic can
 * be off by the hour a transition moves.
 */
export function startOfLocalDate(value: string, timezone: string): Date {
  const match = LOCAL_DATE.exec(value);
  if (!match) throw new Error(`Not a date: ${JSON.stringify(value)}`);
  const utcMidnight = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  // Every zone's offset is within ±14 hours, so the day starts inside this window.
  let before = utcMidnight - 36 * 60 * 60 * 1000;
  let atOrAfter = utcMidnight + 36 * 60 * 60 * 1000;
  while (atOrAfter - before > 1) {
    const middle = Math.floor((before + atOrAfter) / 2);
    if (localDateOf(new Date(middle), timezone) >= value) atOrAfter = middle;
    else before = middle;
  }
  return new Date(atOrAfter);
}

/**
 * Skip dates as a write stores them (Session 10, P9): real dates only (the caller refuses the
 * rest), duplicates removed, days already past in the schedule's zone dropped, ascending. Today
 * stays: its remaining firings are still ahead.
 */
export function normalizeSkipDates(
  dates: readonly string[],
  timezone: string,
  now: Date = new Date(),
): string[] {
  const today = localDateOf(now, timezone);
  return [...new Set(dates)].filter((date) => date >= today).sort(byCodeUnit);
}

/**
 * The next firing that is not on an excluded day and not inside a pause, or `null` for "no
 * upcoming run" (Session 10, P9; decisions B7 and round-2 B4).
 *
 * - A pause covers `[from, pausedUntil)`: the walk starts at `pausedUntil - 1 ms` when that is
 *   later than `from`, so an occurrence exactly at `pausedUntil` fires. O(1), however long the
 *   pause.
 * - An occurrence on a skipped local day (in `timezone`) moves the walk to the START of the next
 *   local day minus 1 ms, so a midnight occurrence on that day is found (the r1 walk, which moved
 *   to the end of the day, lost it and answered a day late).
 * - The walk gives up after `NEXT_FIRE_WALK_DAYS` from where it started and answers null. A cron
 *   whose plain next occurrence is further away (Feb 29) is still returned when no day is excluded.
 * - `nextFireAt` throws rather than answering null; here a throw becomes null, and `onError` hears
 *   about it. This file is bundled by the client, so it never logs: the server-side caller does,
 *   naming the schedule (review r3 S7).
 */
export function nextFireAtExcluding(
  cron: string,
  timezone: string,
  from: Date,
  skipDates: readonly string[] = [],
  pausedUntil: Date | null = null,
  onError?: (error: unknown) => void,
): Date | null {
  let start = from.getTime();
  if (pausedUntil && pausedUntil.getTime() - 1 > start) start = pausedUntil.getTime() - 1;
  const limit = start + NEXT_FIRE_WALK_DAYS * DAY_MS;
  const skipped = new Set(skipDates);
  // Each pass lands on a strictly later local day, so this is bounded by the cap as well.
  for (let pass = 0; pass <= NEXT_FIRE_WALK_DAYS + 1; pass += 1) {
    let candidate: Date;
    try {
      candidate = nextFireAt(cron, timezone, new Date(start));
    } catch (error) {
      onError?.(error);
      return null;
    }
    if (skipped.size === 0) return candidate;
    // Past an excluded day, the walk answers only inside the cap (a yearly cron whose one day is
    // skipped has no allowed firing inside it); the first candidate is returned however far.
    if (pass > 0 && candidate.getTime() > limit) return null;
    const day = localDateOf(candidate, timezone);
    if (!skipped.has(day)) return candidate;
    start = startOfLocalDate(addDaysToDate(day, 1), timezone).getTime() - 1;
    if (start > limit) return null;
  }
  return null;
}

/**
 * The FR-50 friendly description ("At 03:00 AM, Monday through Friday"). Never throws: the
 * description is decoration on a list row, and a schedule that cannot be described is still
 * a schedule that runs.
 *
 * An expression the interval builder writes is described by its window and last run ("Every 30
 * minutes from 10:00 AM until 6:00 PM (last run 5:30 PM)") rather than by cronstrue's "between
 * 10:00 AM and 05:59 PM", which is true and reads like a typo.
 */
export function describeCron(cron: string): string {
  const interval = intervalFromCron(cron);
  if (interval) return describeInterval(interval);
  const yearly = describeYearly(cron);
  if (yearly) return yearly;
  try {
    return cronstrue.toString(cron, { throwExceptionOnParseError: true, verbose: false });
  } catch {
    return cron;
  }
}

/**
 * Validates one expression against one timezone and, on success, hands back the description
 * and the next occurrence so a caller does not parse three times to write one row.
 */
export function validateCron(
  cron: string,
  timezone: string,
  after: Date = new Date(),
): CronValidation {
  const trimmed = cron.trim();
  if (!trimmed) return { valid: false, issue: "A cron expression is required." };
  if (trimmed.length > MAX_CRON_LENGTH) {
    return { valid: false, issue: `A cron expression is at most ${MAX_CRON_LENGTH} characters.` };
  }
  if (!isValidTimezone(timezone)) {
    return { valid: false, issue: `Unknown timezone ${JSON.stringify(timezone)}.` };
  }
  if (!hasFiveFields(trimmed)) {
    return {
      valid: false,
      issue:
        "A cron expression has five fields: minute hour day-of-month month day-of-week. " +
        "Shorthands such as @daily and a seconds field are not stored.",
    };
  }
  let next: Date;
  try {
    // Computing an occurrence, not merely parsing: an unresolvable timezone survives parse().
    next = nextFireAt(trimmed, timezone, after);
  } catch (err) {
    return { valid: false, issue: err instanceof Error ? err.message : String(err) };
  }
  return { valid: true, cron: trimmed, description: describeCron(trimmed), next };
}

/**
 * 12-hour time to 24-hour components. Ported verbatim from the donor, including the 12AM /
 * 12PM special cases, which are the two the naive `+12` gets wrong.
 */
export function parseTime12h(time12h: string): { hour: number; minute: number } {
  const match = /^(\d{2}):(\d{2})(AM|PM)$/i.exec(time12h.trim());
  if (!match) {
    throw new Error(`Invalid 12-hour time format: "${time12h}". Expected HH:MMAM/PM`);
  }
  let hour = Number.parseInt(match[1] as string, 10);
  const minute = Number.parseInt(match[2] as string, 10);
  const period = (match[3] as string).toUpperCase();
  if (hour > 12 || minute > 59) {
    throw new Error(`Invalid 12-hour time format: "${time12h}". Expected HH:MMAM/PM`);
  }
  // 12AM is midnight (0) and 12PM is noon (12); everything else shifts by twelve in the
  // afternoon.
  if (hour === 12) {
    hour = period === "AM" ? 0 : 12;
  } else if (period === "PM") {
    hour += 12;
  }
  return { hour, minute };
}

/**
 * The minutes a schedule's times may use (round 5, owner 2026-10-01: "yes, go with 15-minute
 * steps"). The editor's picker offers only these; the server refuses a NEW or CHANGED expression
 * that fires at any other minute (`createScheduleBodySchema`, `ScheduleService.update`). A
 * schedule stored at another minute before the rule keeps running and keeps saving unchanged.
 */
export const QUARTER_HOUR_MINUTES = [0, 15, 30, 45] as const;

/** The refusal, on the time (`friendly.cronSchedules.{i}`) or on `cron`. */
export const QUARTER_HOUR_ISSUE = "Choose a time on the quarter hour: :00, :15, :30 or :45.";

export function isQuarterHourMinute(minute: number): boolean {
  return (QUARTER_HOUR_MINUTES as readonly number[]).includes(minute);
}

/**
 * The minutes `cron` fires at that are not quarter hours, ascending; empty when every one is.
 * Read from cron-parser's own expansion of the minute field, so `*\/15` and `0,30` pass and
 * `*\/5` and `0-59/20` do not, however they are spelled. An expression that does not parse
 * answers empty: `validateCron` reports it, with a sentence about what is wrong.
 */
export function offQuarterMinutes(cron: string): number[] {
  if (!hasFiveFields(cron.trim())) return [];
  let minutes: readonly number[];
  try {
    minutes = CronExpressionParser.parse(cron.trim()).fields.minute.values;
  } catch {
    return [];
  }
  return [...new Set(minutes)]
    .filter((minute) => !isQuarterHourMinute(minute))
    .sort((a, b) => a - b);
}

/** `2:30 AM`, as a person reads a 12-hour time (`02:30AM` is the stored form). */
export function formatTime12h(hour: number, minute: number): string {
  const period = hour % 24 >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
}

/**
 * The first time in `cronSchedules` that repeats an earlier one, by its index, with the sentence
 * a person reads ("2:30 AM is already in this schedule."). Compared as times, so `02:30AM` and
 * `02:30am` are the same one. Null when every time is different; a time that does not parse is
 * skipped here (the format rule reports it).
 */
export function duplicateTime(
  cronSchedules: readonly string[],
): { index: number; issue: string } | null {
  const seen = new Set<string>();
  for (const [index, slot] of cronSchedules.entries()) {
    let parsed: { hour: number; minute: number };
    try {
      parsed = parseTime12h(slot);
    } catch {
      continue;
    }
    const key = `${parsed.hour}:${parsed.minute}`;
    if (seen.has(key)) {
      return {
        index,
        issue: `${formatTime12h(parsed.hour, parsed.minute)} is already in this schedule.`,
      };
    }
    seen.add(key);
  }
  return null;
}

export const WINDOW_ORDER_ISSUE =
  "The window must end after it starts on the same day. Create a second schedule for the hours after midnight.";

export const WHOLE_HOUR_ISSUE = "Use a whole hour such as 10:00AM.";

export const INTERVAL_STEP_ISSUE = "Choose every 15, 30 or 60 minutes.";

/** A window edge as an hour of the day, 0..23; `12:00AM` as an END is 24 (midnight after). */
function windowHour(value: string, edge: "start" | "end"): number {
  let parsed: { hour: number; minute: number };
  try {
    parsed = parseTime12h(value);
  } catch {
    throw new Error(WHOLE_HOUR_ISSUE);
  }
  if (parsed.minute !== 0) throw new Error(WHOLE_HOUR_ISSUE);
  return edge === "end" && parsed.hour === 0 ? 24 : parsed.hour;
}

/**
 * The interval's window as hours: `start` included (0..23), `end` excluded (1..24). Throws the
 * sentence the editor and the API show for an edge off the hour or a window that does not end
 * after it starts.
 */
export function intervalWindow(
  schedule: Pick<FriendlyIntervalSchedule, "windowStart" | "windowEnd">,
): { start: number; end: number } {
  const start = windowHour(schedule.windowStart, "start");
  const end = windowHour(schedule.windowEnd, "end");
  if (end <= start) throw new Error(WINDOW_ORDER_ISSUE);
  return { start, end };
}

function dayOfWeekField(weekDays: readonly number[]): string {
  return weekDays.length > 0 ? [...new Set(weekDays)].sort((a, b) => a - b).join(",") : "*";
}

/** The interval as the one expression `schedules.cron` holds: `*\/30 10-17 * * 1,2,3,4,5`. */
export function intervalToCron(schedule: FriendlyIntervalSchedule): string {
  if (!(INTERVAL_MINUTES as readonly number[]).includes(schedule.intervalMinutes)) {
    throw new Error(INTERVAL_STEP_ISSUE);
  }
  const { start, end } = intervalWindow(schedule);
  const minute = schedule.intervalMinutes === 60 ? "0" : `*/${schedule.intervalMinutes}`;
  const hours = end - start === 1 ? `${start}` : `${start}-${end - 1}`;
  return `${minute} ${hours} * * ${dayOfWeekField(schedule.weekDays)}`;
}

const INTERVAL_CRON = /^(0|\*\/15|\*\/30) (\d{1,2})(?:-(\d{1,2}))? \* \* (\*|[0-6](?:,[0-6])*)$/;

/** A whole hour of the day (0..24) in the stored 12-hour form, `10:00AM`; 24 is `12:00AM`. */
function wholeHour12h(hour: number): string {
  const period = hour % 24 >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(hour12).padStart(2, "0")}:00${period}`;
}

/** The interval an expression's minute field means: `0` hourly, `*\/15` and `*\/30`. */
function intervalMinutesOf(minute: string): FriendlyIntervalSchedule["intervalMinutes"] {
  if (minute === "0") return 60;
  return minute === "*/15" ? 15 : 30;
}

/**
 * An expression the interval builder writes, read back as that interval; null for anything else
 * (`0 2 * * *` is one time a day, which the times builder owns). Trusted only when it
 * round-trips through `intervalToCron`, so a day list the builder would write differently (`5,1`,
 * `1-5`) stays a custom expression and a rename never re-times it.
 */
export function intervalFromCron(cron: string | null): FriendlyIntervalSchedule | null {
  if (!cron) return null;
  const normalized = cron.trim().split(/\s+/).join(" ");
  const match = INTERVAL_CRON.exec(normalized);
  if (!match) return null;
  const minute = match[1] as string;
  const start = Number.parseInt(match[2] as string, 10);
  const last = match[3] === undefined ? start : Number.parseInt(match[3], 10);
  const days = match[4] as string;
  if (minute === "0" && match[3] === undefined) return null;
  if (start > 23 || last > 23 || last < start) return null;
  const interval: FriendlyIntervalSchedule = {
    frequency: "Interval",
    intervalMinutes: intervalMinutesOf(minute),
    windowStart: wholeHour12h(start),
    windowEnd: wholeHour12h(last + 1),
    weekDays: days === "*" ? [] : days.split(",").map((day) => Number.parseInt(day, 10)),
  };
  try {
    return intervalToCron(interval) === normalized ? interval : null;
  } catch {
    return null;
  }
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** "Monday through Friday", "on Saturday and Sunday", "on Monday, Wednesday and Friday". */
function describeDays(weekDays: readonly number[]): string | null {
  const days = [...new Set(weekDays)];
  if (days.length === 0 || days.length === 7) return null;
  // Read Monday first, as a working week is read: Sunday (0) sorts as 7.
  const ordered = days.map((day) => (day === 0 ? 7 : day)).sort((a, b) => a - b);
  const names = ordered.map((day) => DAY_NAMES[day % 7] as string);
  const contiguous = ordered.every(
    (day, index) => index === 0 || day - (ordered[index - 1] as number) === 1,
  );
  if (contiguous && names.length >= 3) return `${names[0]} through ${names[names.length - 1]}`;
  if (names.length === 1) return `on ${names[0]}`;
  return `on ${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "Every 30 minutes from 10:00 AM until 6:00 PM (last run 5:30 PM), Monday through Friday". */
export function describeInterval(schedule: FriendlyIntervalSchedule): string {
  const { start, end } = intervalWindow(schedule);
  const step = schedule.intervalMinutes;
  const every = step === 60 ? "Every hour" : `Every ${step} minutes`;
  const lastMinutes = end * 60 - step;
  const last = formatTime12h(Math.floor(lastMinutes / 60), lastMinutes % 60);
  const days = describeDays(schedule.weekDays);
  // An end of 24 is midnight at the end of the day; "until 12:00 AM" alone reads as the start.
  const until = end === 24 ? "midnight" : formatTime12h(end, 0);
  return `${every} from ${formatTime12h(start, 0)} until ${until} (last run ${last})${
    days ? `, ${days}` : ""
  }`;
}

const YEARLY_CRON = /^(\d{1,2}) (\d{1,2}(?:,\d{1,2})*) (\d{1,2}) (\d{1,2}) \*$/;

/**
 * One day of the year an expression names, `m h D M *` with one number each for D and M, read
 * back as the Yearly builder's date; null for anything else. Trusted only when the date is one
 * the builder accepts (`yearDateIssue`): `0 3 29 2 *` stays a custom expression.
 */
export function yearDateFromCron(cron: string | null): YearDate | null {
  if (!cron) return null;
  const match = YEARLY_CRON.exec(cron.trim().split(/\s+/).join(" "));
  if (!match) return null;
  const value = { month: Number(match[4]), day: Number(match[3]) };
  if (String(value.month) !== match[4] || String(value.day) !== match[3]) return null;
  return yearDateIssue(value) ? null : value;
}

/** "Every year on March 6 at 5:00 PM", for an expression the Yearly builder writes. */
function describeYearly(cron: string): string | null {
  const date = yearDateFromCron(cron);
  if (!date) return null;
  const [minute, hours] = cron.trim().split(/\s+/) as [string, string];
  const minuteValue = Number(minute);
  if (minuteValue > 59 || String(minuteValue) !== minute) return null;
  const hourValues = hours.split(",").map(Number);
  if (hourValues.some((hour, i) => hour > 23 || String(hour) !== hours.split(",")[i])) return null;
  const times = hourValues.map((hour) => formatTime12h(hour, minuteValue));
  const at = times.length === 1 ? times[0] : `${times.slice(0, -1).join(", ")} and ${times.at(-1)}`;
  return `Every year on ${MONTH_NAMES[date.month - 1]} ${date.day} at ${at}`;
}

/**
 * The friendly builder: one 5-field expression per time slot (donor port).
 *
 * `Daily` ignores both day lists; `Weekly` uses `weekDays`; `Monthly` uses `monthDays`. A
 * frequency whose list is empty degrades to `*`, which is the donor's behaviour and reads as
 * "every day" rather than failing a save. An `Interval` is one expression (`intervalToCron`).
 * `Yearly` (round 10, C1) writes its one date, `m h D M *`, and throws without a real one.
 */
export function friendlyToCron(schedule: FriendlySchedule): string[] {
  if (schedule.frequency === "Interval") return [intervalToCron(schedule)];
  const { frequency, cronSchedules, weekDays, monthDays } = schedule;
  if (frequency === "Yearly") {
    const date = schedule.yearDate;
    if (!date) throw new Error("Choose the month and day this schedule runs.");
    const issue = yearDateIssue(date);
    if (issue) throw new Error(issue.issue);
    return cronSchedules.map((timeSlot) => {
      const { hour, minute } = parseTime12h(timeSlot);
      return `${minute} ${hour} ${date.day} ${date.month} *`;
    });
  }
  return cronSchedules.map((timeSlot) => {
    const { hour, minute } = parseTime12h(timeSlot);
    const dayOfMonth =
      frequency === "Monthly" && monthDays.length > 0
        ? [...new Set(monthDays)].sort((a, b) => a - b).join(",")
        : "*";
    const dayOfWeek =
      frequency === "Weekly" && weekDays.length > 0
        ? [...new Set(weekDays)].sort((a, b) => a - b).join(",")
        : "*";
    return `${minute} ${hour} ${dayOfMonth} * ${dayOfWeek}`;
  });
}

/**
 * The friendly builder collapsed to the single expression `schedules.cron` holds.
 *
 * The donor stored an array because Cosmos could; one row here holds one expression. Slots
 * that differ only in the hour merge into one expression (`0 1,19 * * 1,3,5`), because a
 * cron hour field is a list. Slots whose minutes differ cannot be expressed in one 5-field
 * expression at all, so that is a validation error naming the fix rather than a silent drop
 * of every slot but the first.
 */
export function friendlyToCronExpression(schedule: FriendlySchedule): string {
  if (schedule.frequency === "Interval") return intervalToCron(schedule);
  const expressions = friendlyToCron(schedule);
  if (expressions.length === 0) {
    throw new Error("A friendly schedule needs at least one time.");
  }
  // The same time twice is refused, not merged (2026-09-28, owner: "I was able to save the
  // 2.30 AM time twice"). The zod schema says so first, with the field path; this is the rule
  // for every other caller.
  const duplicate = duplicateTime(schedule.cronSchedules);
  if (duplicate) throw new Error(duplicate.issue);
  const minutes = new Set(schedule.cronSchedules.map((slot) => parseTime12h(slot).minute));
  if (minutes.size > 1) {
    throw new Error(
      "Times in one schedule must share the same minute past the hour (:00, :15, :30 or :45). " +
        "Create a second schedule for a time at a different minute.",
    );
  }
  const first = expressions[0] as string;
  const [minute, , dayOfMonth, month, dayOfWeek] = first.split(" ");
  const hours = [...new Set(schedule.cronSchedules.map((slot) => parseTime12h(slot).hour))].sort(
    (a, b) => a - b,
  );
  return `${minute} ${hours.join(",")} ${dayOfMonth} ${month} ${dayOfWeek}`;
}

/**
 * The earliest next occurrence across several expressions, as an ISO string (donor port).
 * Kept for the Cosmos scheduler documents, which carry `cronExpressions[]`; the row-level
 * path uses `nextFireAt`.
 */
export function calculateNextRunAt(
  cronExpressions: readonly string[],
  timezone: string,
  currentDate: Date = new Date(),
): string | null {
  let earliest: Date | null = null;
  for (const cron of cronExpressions) {
    try {
      const next = nextFireAt(cron, timezone, currentDate);
      if (!earliest || next < earliest) earliest = next;
    } catch {
      // An expression that does not parse is skipped; `validateCronExpressions` is what
      // refuses the save, because a null written here leaves a schedule dormant while it
      // still reads as enabled.
      continue;
    }
  }
  return earliest ? earliest.toISOString() : null;
}

/** The expressions that do not parse under this timezone; empty means all are valid. */
export function validateCronExpressions(
  cronExpressions: readonly string[],
  timezone: string,
): string[] {
  return cronExpressions.filter((cron) => !validateCron(cron, timezone).valid);
}

// ---------------------------------------------------------------------------------------------
// Once: specific dates (round 10 wave 2, S3, design §1.5). A dates schedule has no expression;
// it fires at each of its instants (`schedules.run_dates`, UTC), each written as a local date and
// time in the schedule's zone.
// ---------------------------------------------------------------------------------------------

/** At most this many dates on one Once schedule. */
export const MAX_RUN_DATES = 20;

/** A new Once date is at most this many days ahead (the server refuses one later). */
export const MAX_RUN_DATE_DAYS = 400;

/** A local time that a daylight-saving change skips ("2:30 AM on March 8" in Pacific). */
export class LocalTimeGapError extends Error {
  override readonly name = "LocalTimeGapError";
  constructor(
    readonly date: string,
    readonly time12h: string,
  ) {
    super(`That time does not exist on ${date}`);
  }
}

const wallFormatters = new Map<string, Intl.DateTimeFormat>();

function wallFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = wallFormatters.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    wallFormatters.set(timezone, formatter);
  }
  return formatter;
}

/** The wall clock of `at` in `timezone`, as milliseconds of a UTC date with the same fields. */
function wallClockMs(at: number, timezone: string): number {
  const parts = wallFormatter(timezone).formatToParts(new Date(at));
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((entry) => entry.type === type)?.value ?? "0");
  return Date.UTC(part("year"), part("month") - 1, part("day"), part("hour") % 24, part("minute"));
}

/**
 * The instant a local date and 12-hour time name in `timezone` (`2026-03-06`, `05:00PM`).
 * A time a daylight-saving change skips throws `LocalTimeGapError`; a time it repeats (1:30 AM on
 * the November change) is the EARLIER instant. Exact to the minute: the offsets of every zone in
 * use are whole minutes.
 */
export function localDateTimeToInstant(date: string, time12h: string, timezone: string): Date {
  const match = LOCAL_DATE.exec(date);
  if (!match || !isRealDate(date)) throw new Error(`Not a date: ${JSON.stringify(date)}`);
  const { hour, minute } = parseTime12h(time12h);
  const wanted = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), hour, minute);
  // The zone's offset is the wall clock less the instant; try the offsets in force a day either
  // side of the wanted wall time, which are the only two a transition can put in play.
  const offsets = new Set<number>();
  for (const probe of [wanted - DAY_MS, wanted, wanted + DAY_MS]) {
    offsets.add(wallClockMs(probe, timezone) - probe);
  }
  const matches = [...offsets]
    .map((offset) => wanted - offset)
    .filter((instant) => wallClockMs(instant, timezone) === wanted)
    .sort((a, b) => a - b);
  if (matches.length === 0) throw new LocalTimeGapError(date, time12h);
  return new Date(matches[0] as number);
}

/**
 * The next firing of a dates schedule: the first of `runDates` strictly after `from`, and not
 * inside a pause (`[from, pausedUntil)`; an instant exactly at `pausedUntil` fires). Null when
 * none is left: the schedule has completed (S5).
 */
export function nextRunDate(
  runDates: ReadonlyArray<Date | string>,
  from: Date,
  pausedUntil: Date | null = null,
): Date | null {
  const floor = Math.max(from.getTime() + 1, pausedUntil ? pausedUntil.getTime() : -Infinity);
  let best: number | null = null;
  for (const value of runDates) {
    const at = (typeof value === "string" ? new Date(value) : value).getTime();
    if (Number.isNaN(at) || at < floor) continue;
    if (best === null || at < best) best = at;
  }
  return best === null ? null : new Date(best);
}

/** The local date and 12-hour time of `at` in `timezone` (`2026-03-06`, `05:00PM`). */
export function instantToLocalDateTime(at: Date, timezone: string): { date: string; time: string } {
  const wall = new Date(wallClockMs(at.getTime(), timezone));
  return {
    date: wall.toISOString().slice(0, 10),
    time: formatTime12hStored(wall.getUTCHours(), wall.getUTCMinutes()),
  };
}

/** `17, 0` → `05:00PM`, the stored 12-hour form. */
function formatTime12hStored(hour: number, minute: number): string {
  const period = hour < 12 ? "AM" : "PM";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(twelve).padStart(2, "0")}:${String(minute).padStart(2, "0")}${period}`;
}

/**
 * A schedule as the freshness rule reads it (skyMonitor's `freshness` check and the web's Level B
 * reliability read, #828). Only an enabled schedule with a cron fires; a "Once" schedule (`cron`
 * NULL, `run_dates`) does not count, so a connection only on one has no limit.
 */
export interface FiringSchedule {
  cron: string | null;
  timezone: string;
  enabled: boolean;
  /** Local days (`YYYY-MM-DD`, in `timezone`) on which the schedule does not fire. */
  skipDates: readonly string[];
  pausedUntil: Date | null;
}

/** The schedules that fire on a cron: enabled, with a non-blank expression. */
export function firingSchedules<T extends FiringSchedule>(schedules: readonly T[]): T[] {
  return schedules.filter((s) => s.enabled && s.cron !== null && s.cron.trim() !== "");
}

/**
 * The earliest firing strictly after `after` over the schedules, skipping their skip dates and
 * ignoring any pause. Null when no schedule fires within the walk (or none parses). Moved here
 * from `monitor/checks/freshness.ts` (#828) so skyMonitor and the web share one rule without
 * either importing the other.
 */
export function firstFiringAfter(schedules: readonly FiringSchedule[], after: Date): Date | null {
  let earliest: Date | null = null;
  for (const schedule of firingSchedules(schedules)) {
    const next = nextFireAtExcluding(
      schedule.cron as string,
      schedule.timezone,
      after,
      schedule.skipDates,
      null,
    );
    if (next && (!earliest || next.getTime() < earliest.getTime())) earliest = next;
  }
  return earliest;
}
